import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Form, Input, Modal, Space, Tag,
  Statistic, Row, Col, Popconfirm, message, Empty, Select, Skeleton,
} from 'antd';
import {
  PlusOutlined, ImportOutlined, BarChartOutlined,
  EditOutlined, DeleteOutlined, BookOutlined, SearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { getCourses, createCourse, updateCourse, deleteCourse } from '../services/api';

export default function CoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const res = await getCourses();
      setCourses(res.data.data || []);
    } catch {
      message.error('Failed to load courses');
    }
    setLoading(false);
  };

  useEffect(() => { fetchCourses(); }, []);

  const handleAdd = () => {
    setEditingCourse(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    form.setFieldsValue({
      course_id: course.course_id,
      display_name: course.display_name,
      pacing_type: course.pacing_type,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingCourse) {
        await updateCourse(editingCourse.course_key, values);
        message.success('Course updated');
      } else {
        await createCourse(values);
        message.success('Course added');
      }
      setModalOpen(false);
      fetchCourses();
    } catch (err) {
      if (err.response) message.error(err.response.data.error);
    }
  };

  const handleDelete = async (courseKey) => {
    try {
      await deleteCourse(courseKey);
      message.success('Course and all related data deleted');
      fetchCourses();
    } catch {
      message.error('Failed to delete course');
    }
  };

  const filteredCourses = courses.filter((c) =>
    c.course_id.toLowerCase().includes(searchText.toLowerCase()) ||
    (c.display_name || '').toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <h2><SettingOutlined /> Courses ({courses.length})</h2>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder="Search courses..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 250 }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Course
            </Button>
          </Space>
        </Col>
      </Row>

      {loading ? (
        <Row gutter={[16, 16]}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Col xs={24} lg={12} xl={8} key={i}>
              <Card>
                <Skeleton active paragraph={{ rows: 4 }} />
              </Card>
            </Col>
          ))}
        </Row>
      ) : filteredCourses.length === 0 ? (
        <Empty description="No courses yet. Click 'Add Course' to get started." />
      ) : (
        <Row gutter={[16, 16]}>
          {filteredCourses.map((course) => (
            <Col xs={24} lg={12} xl={8} key={course.course_key}>
              <Card
                title={
                  <Space>
                    <BookOutlined />
                    <span>{course.course_code} ({course.course_run})</span>
                  </Space>
                }
                extra={
                  <Tag color={course.stats?.total_events > 0 ? 'green' : 'default'}>
                    {course.stats?.total_events > 0 ? 'Has Data' : 'No Data'}
                  </Tag>
                }
                actions={[
                  <Button
                    type="link"
                    icon={<ImportOutlined />}
                    onClick={() => navigate(`/courses/${course.course_key}/import`)}
                  >
                    Import
                  </Button>,
                  <Button
                    type="link"
                    icon={<BarChartOutlined />}
                    onClick={() => navigate(`/courses/${course.course_key}/analytics`)}
                  >
                    Analytics
                  </Button>,
                  <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(course)}>
                    Edit
                  </Button>,
                  <Popconfirm
                    title="Delete this course and ALL its data?"
                    onConfirm={() => handleDelete(course.course_key)}
                    okText="Delete"
                    okType="danger"
                  >
                    <Button type="link" danger icon={<DeleteOutlined />}>Delete</Button>
                  </Popconfirm>,
                ]}
              >
                <p style={{ color: '#666', marginBottom: 12 }}>
                  {course.display_name || course.course_id}
                </p>
                <p style={{ fontSize: 12, color: '#999', marginBottom: 16 }}>
                  <strong>Course ID:</strong> {course.course_id}
                </p>

                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Events"
                      value={course.stats?.total_events || 0}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Learners"
                      value={course.stats?.total_learners || 0}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Last Import"
                      value={
                        course.stats?.last_event
                          ? new Date(course.stats.last_event).toLocaleDateString('th-TH')
                          : '-'
                      }
                      valueStyle={{ fontSize: 14 }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Add/Edit Course Modal */}
      <Modal
        title={editingCourse ? 'Edit Course' : 'Add Course'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingCourse ? 'Save' : 'Add'}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="course_id"
            label="Course ID"
            rules={[
              { required: true, message: 'Please enter Course ID' },
              {
                pattern: /^course-v1:.+\+.+\+.+$/,
                message: 'Format: course-v1:Org+Course+Run',
              },
            ]}
            help="e.g. course-v1:MU+CS101+2026_T1"
          >
            <Input
              placeholder="course-v1:Org+Course+Run"
              disabled={!!editingCourse}
            />
          </Form.Item>
          <Form.Item name="display_name" label="Course Name (optional)">
            <Input placeholder="e.g. Introduction to Computer Science" />
          </Form.Item>
          <Form.Item name="pacing_type" label="Pacing Type (optional)">
            <Select placeholder="Select pacing type" allowClear>
              <Select.Option value="self_paced">Self-Paced</Select.Option>
              <Select.Option value="instructor_paced">Instructor-Paced</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function SettingOutlined2() { return null; }
